import { css } from 'aphrodite/no-important';
import {
  BioPagination,
  ILanguage,
  KeynoteVideo,
  LanguageSelector,
  LoadingSpinner,
  ModalWrapper,
  SharedCarousel,
  TrackSessions,
  VideoModal,
  VideoTile,
} from 'components';
import { useScrollToTopOnMount } from 'hooks';
import useSessionVideos from 'hooks/useSessionVideos';
import { GenericObject, IBio, VideoData } from 'models';
import React, { FC, useEffect, useMemo, useState } from 'react';
import api from 'util/api';
import { ROUTE } from 'util/constants';
import { GAEventAction, GAEventCategory, GAEventLabel, trackGoogleEvent } from 'util/google-helpers';
import { checkIfTrue } from 'util/helpers';
import { style } from './style';

interface Props {
  route: String;
  text: GenericObject;
}

export const Session: FC<Props> = ({ text, route }) => {
  useScrollToTopOnMount();

  const styles = style({});
  const { settings, language_settings, speaker_bio_button_text, speaker_bios_button_text } = text;
  const [bios, setBios] = useState<IBio[] | null>(null);

  // Generic Flags
  const isTrackSessionsEnabled = checkIfTrue(settings.display_track_sessions);
  const isVideoPlaysEnabled = checkIfTrue(settings.enable_video_plays);
  const [isBioModalOpen, setBioModalOpen] = useState(false);

  // Language Data
  const languages = language_settings?.languages;
  let mainLanguage = undefined;
  if (languages && languages.length)
    mainLanguage = languages.find((lan: any) => checkIfTrue(lan.active)) || languages[0];
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage | undefined>(mainLanguage);

  // Videos Data & Flags
  const allVideos = useSessionVideos(route.toString());

  const _keynoteVideos = useMemo(() => allVideos.filter((vid) => checkIfTrue(vid.is_keynote)), [allVideos]);
  const [keynoteVideos, setKeyNoteVideos] = useState<VideoData[]>([]);
  const _filteredVideos = useMemo(() => allVideos.filter((vid) => !checkIfTrue(vid.is_keynote)), [allVideos]);
  const [filteredVideos, setFilteredVideos] = useState<VideoData[]>([]);

  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [videoModalData, setVideoModalData] = useState<VideoData>();

  const MAX_VOD_COLUMN_COUNT = 4;
  const MIN_VOD_COLUMN_COUNT = 3;
  const vodColumnCount = route === ROUTE.PLENARY_SESSION ? MAX_VOD_COLUMN_COUNT : MIN_VOD_COLUMN_COUNT;
  const [activeVideo, setActiveVideo] = useState<any>(null); // TODO:: Remove. We don't use this.

  useEffect(() => {
    if (!_filteredVideos || _filteredVideos.length < 1) return;
    const filteredVideosWithCorrectLanguage = getVideosWithLanguage(_filteredVideos, selectedLanguage);
    setFilteredVideos(filteredVideosWithCorrectLanguage);
    setActiveVideo(filteredVideosWithCorrectLanguage[0]);
  }, [route, _filteredVideos, selectedLanguage]);

  useEffect(() => {
    if (!_keynoteVideos || _keynoteVideos.length < 1) {
      setKeyNoteVideos([]);
      return;
    }
    const keynoteVideosWithCorrectLanguage = getVideosWithLanguage([..._keynoteVideos], selectedLanguage);
    setKeyNoteVideos(keynoteVideosWithCorrectLanguage);
  }, [route, _keynoteVideos, selectedLanguage]);

  const getVideosWithLanguage = (videos: any[], language: ILanguage | undefined): any[] => {
    if (!language) return videos;
    // This method override the video_id and video_url depending on the language
    // This is done so we don't need to check what to use: video_id or video_id_jp on other components
    // We will always use video_id. But with this method, that video_id content may change from video_id to video_id_jp
    return videos.map((video) => {
      const transformedVideo = { ...video };
      transformedVideo.video_id = video[language.video_id_identifier];
      transformedVideo.video_url = video[language.video_url_identifier];
      transformedVideo.speaker_bio_urls = video[language.video_speaker_bio_urls_identifier];
      return transformedVideo;
    });
  };

  async function handleBioClick(urls: string[]) {
    const response = await api.fetchBios(urls);
    setBios(response);
    setBioModalOpen(true);

    trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, GAEventLabel.SPEAKER_BIO);
  }

  const handleVideoClick = (video: VideoData) => {
    window.scrollTo(0, 0);
    setVideoModalData(video);
    setVideoModalOpen(true);
  };

  const renderVideoTiles = () => {
    const columns = Math.min(vodColumnCount, Math.max(filteredVideos.length, MIN_VOD_COLUMN_COUNT));
    return filteredVideos.map((data: VideoData, index: number) => {
      return (
        <VideoTile
          disabled={!isVideoPlaysEnabled}
          videoObject={{ ...data }}
          onVideoClick={handleVideoClick}
          onBioClick={handleBioClick}
          voiceOverBadge={selectedLanguage?.voiceover_badge_text}
          copy={{ speaker_bio_button_text, speaker_bios_button_text }}
          maxColumnCount={columns}
          key={index}
        />
      );
    });
  };

  if (!activeVideo) return <LoadingSpinner></LoadingSpinner>;

  return (
    <>
      <main className={css(styles.main)}>
        {selectedLanguage ? (
          <LanguageSelector
            label={language_settings.language_selector_label}
            languages={language_settings.languages}
            selectedLanguage={selectedLanguage}
            onLanguageSelectedCallback={(language: ILanguage) => setSelectedLanguage(language)}
          />
        ) : null}

        {keynoteVideos.map((keynoteVideo, i) => (
          <KeynoteVideo
            key={i}
            videoData={keynoteVideo}
            showBioButton={false}
            onBioClick={handleBioClick}
            copy={text}
            voiceOverBadgeText={selectedLanguage?.voiceover_badge_text}
          />
        ))}

        <section className={css(styles.onDemandGrid)}>
          <div className={css(styles.videoTileContainer)}>{renderVideoTiles()}</div>
        </section>

        {isTrackSessionsEnabled && <TrackSessions contentUrl={text.settings.track_sessions_data_url} />}

        <div className={css(styles.carousel)}>
          <SharedCarousel />
        </div>
      </main>
      {/* Speaker Bio Modal */}
      {!videoModalData ? null : (
        <VideoModal
          active={isVideoModalOpen}
          video={videoModalData}
          videos={filteredVideos}
          autoplay
          autoplayNext
          onRequestClose={() => {
            setVideoModalOpen(false);
            setVideoModalData(undefined);
          }}
        />
      )}
      <ModalWrapper
        isOpen={isBioModalOpen}
        options={{ fillSmallerScreens: true, preventScroll: false }}
        onRequestClose={() => setBioModalOpen(false)}
        onAfterClose={() => setBios(null)}
        customStyles={{
          modalContent: {
            desktop: { padding: '80px 26px 30px 69px' },
            tablet: { padding: '80px 20px 56px 30px' },
            mobile: { padding: '80px 20px 40px 20px' },
          },
        }}>
        <BioPagination bios={bios || []} />
      </ModalWrapper>
    </>
  );
};
